import React, { useState, useEffect } from 'react';
import { Calendar, Flame, Heart, Award, TrendingUp, Share2 } from 'lucide-react';
import { nativeShare } from '../../utils/capacitor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * WeeklyWrapUp — Auto-generated summary card every Sunday.
 * Sessions, streak, hearts earned, badges, top skill. Shareable.
 */
const WeeklyWrapUp = () => {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Only show on Sunday or if not dismissed this week
    const lastDismiss = localStorage.getItem('hibff_weekly_wrapup_dismiss');
    const now = new Date();
    const weekKey = `${now.getFullYear()}-W${Math.ceil(now.getDate() / 7)}`;
    if (lastDismiss === weekKey) { setDismissed(true); return; }

    const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
    if (!token) return;
    fetch(`${API_URL}/api/engagement/v2/weekly-wrapup`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(setData).catch(() => {});
  }, []);

  const handleShare = () => {
    if (!data) return;
    nativeShare({ title: 'My HiBFF Week', text: data.shareable_text, url: window.location.origin });
  };

  const handleDismiss = () => {
    const now = new Date();
    const weekKey = `${now.getFullYear()}-W${Math.ceil(now.getDate() / 7)}`;
    localStorage.setItem('hibff_weekly_wrapup_dismiss', weekKey);
    setDismissed(true);
  };

  if (!data || dismissed) return null;

  const stats = [
    { icon: Calendar, label: 'Sessions', value: data.sessions, color: 'text-blue-500' },
    { icon: Flame, label: 'Streak', value: `${data.streak} days`, color: 'text-orange-500' },
    { icon: Heart, label: 'Hearts', value: `+${data.hearts_earned}`, color: 'text-red-500' },
    { icon: Award, label: 'Badges', value: data.badges_earned, color: 'text-purple-500' },
  ];

  return (
    <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-white to-purple-50 overflow-hidden mb-4" data-testid="weekly-wrapup">
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <TrendingUp size={18} />
          <span className="text-sm font-bold">Your Week in Review</span>
        </div>
        <span className="text-xs text-white/70">{data.week_ending}</span>
      </div>

      <div className="p-4">
        <p className="text-sm text-gray-600 mb-3">Great work this week, <strong>{data.user_name}</strong>!</p>

        {/* Stats grid */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          {stats.map(s => (
            <div key={s.label} className="text-center p-2 bg-white rounded-lg border border-gray-100">
              <s.icon size={16} className={`${s.color} mx-auto mb-1`} />
              <p className="text-sm font-extrabold text-gray-800">{s.value}</p>
              <p className="text-[10px] text-gray-400">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Top skill */}
        <div className="bg-white rounded-lg border border-gray-100 p-3 mb-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
            <TrendingUp size={14} className="text-emerald-600" />
          </div>
          <div>
            <p className="text-xs text-gray-400">Top Skill Improved</p>
            <p className="text-sm font-bold text-gray-800">{data.top_skill}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={handleShare}
            className="flex-1 flex items-center justify-center gap-2 py-2 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 transition-all"
            data-testid="weekly-wrapup-share"
          >
            <Share2 size={14} /> Share
          </button>
          <button
            onClick={handleDismiss}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 font-medium"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default WeeklyWrapUp;
